import { useHistory } from 'react-router-dom';
import { ModalV2Dialog } from '../../../../core/components';
import { LocationState } from '../../../../core/types/location';
import { useStore } from '../../../../core/context/store';
import { useCountdown } from '../../../../core/hooks';
import { standardSequencesActions } from '../../actions';
import { useCloseSession } from '../../../courses/api/session-mutations';
import { TIME_IN_SEC_UNTIL_SESSION_IS_CLOSED_DUE_TO_INACTIVITY } from '../../constants';
import { SessionProps } from './models';

type UserIsIdleModalProps = {
  labId?: number;
  labName?: string;
  onClose: VoidFunction;
  sessionId: number;
};

const getTimeRemainingAsString = (secondsRemaining: number) => {
  if (secondsRemaining >= 60 * 2) return `${Math.floor(secondsRemaining / 60)} minutes`;
  if (secondsRemaining >= 60) return '1 minute';
  if (secondsRemaining > 1) return `${secondsRemaining} seconds`;
  return '1 second';
};

export const InactivityNoticeModal = ({ labId, labName, onClose, sessionId }: UserIsIdleModalProps): JSX.Element => {
  const history = useHistory<LocationState>();
  const [, activeSessionStoreActions] = useStore<SessionProps>('active-session');
  const { mutate: closeSession, isLoading: isClosingSession } = useCloseSession({});

  const onCountdownFinished = () => {
    closeSession(
      { sessionId, labId: null },
      {
        // We are redirecting the user here even when closing session fails as it is better that the user can move on and reopen the lab
        onSettled: () => {
          history.replace('/courses', { labId, labName, navigationReason: 'timeout' });
          activeSessionStoreActions.dispatch(standardSequencesActions.clearSessionData());
          onClose();
        },
      }
    );
  };

  const secondsRemainingUntilTimeout = useCountdown({
    enable: true,
    nbrOfSeconds: TIME_IN_SEC_UNTIL_SESSION_IS_CLOSED_DUE_TO_INACTIVITY,
    callback: onCountdownFinished,
  });

  const timeRemainingAsString = getTimeRemainingAsString(secondsRemainingUntilTimeout);

  const onCloseHandler = isClosingSession ? () => {} : onClose;

  return (
    <ModalV2Dialog
      onClose={onCloseHandler}
      title="Inactivity notice"
      primaryButton={{ onClick: onCloseHandler, children: 'Stay', isLoading: isClosingSession }}
      closeOnOutsideClick
      withCloseButton
    >
      <>
        <p className="simulator-inactivity-notice-modal__description">
          Due to inactivity the simulator will be closed in:{' '}
          <span className="simulator-inactivity-notice-modal__description--bold">
            {isClosingSession ? '0 seconds' : timeRemainingAsString}
          </span>
        </p>
        <p className="simulator-inactivity-notice-modal__description">Press Stay to continue using the simulator.</p>
      </>
    </ModalV2Dialog>
  );
};
